// Per-building-item calculation.
//
// Pure function: takes a BuildingItem, a Parcel, and a MarketPriceSet,
// returns an ItemCalculationResult. All messages (warnings, errors,
// info notes) collected during the run are emitted via the supplied
// MessageSink.

import { DEFAULT_FACTOR, type FactorChain } from "../types/factor-chain.js";
import type { BuildingItem } from "../types/building-item.js";
import type { ItemCalculationResult } from "../types/calculation-result.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";

import {
  applyCommercialOfficePriceFloor,
  calculateLandTermFactor,
  getAffordableHousingFactor,
  getApplicablePriceType,
  getBuildingTypeFactor,
  getCommercialFloorFactor,
  getIndustryOrientationFactor,
  getIndustryProjectTypeFactor,
  getMarketPrice,
  getPropertyConditionFactor,
  getUndergroundSpaceFactor,
} from "./factor-helpers.js";
import type { MessageSink } from "./message-sink.js";

export interface ItemCalculationInput {
  readonly item: BuildingItem;
  readonly parcel: Parcel;
  readonly prices: MarketPriceSet;
  readonly sink: MessageSink;
}

/**
 * Per CALCULATION_MVP.md §3, government-owned items produce
 * `itemLandPrice = 0` and an info-level note.
 */
export function calculateItem({
  item,
  parcel,
  prices,
  sink,
}: ItemCalculationInput): ItemCalculationResult {
  // --- 1. Applicable price type -------------------------------------------------
  const applicablePriceType = getApplicablePriceType(item.buildingType, sink);
  if (!applicablePriceType) {
    return buildEmptyResult(item, sink, undefined, undefined);
  }

  // --- 2. Market price (with office-floor handling) ----------------------------
  const preliminaryCommercialFloor = getCommercialFloorFactor(
    item.buildingType,
    item.isUnderground,
    item.commercialFloor,
    sink,
  );

  const floor = applyCommercialOfficePriceFloor(
    item.buildingType,
    item.isUnderground,
    item.commercialFloor,
    preliminaryCommercialFloor,
    prices.commercial,
    prices.office,
    sink,
  );
  const commercialFloorFactor = floor.commercialFloorFactor;
  if (floor.note) {
    sink.info("marketPrice", floor.note);
  }

  let marketPrice: number | undefined;
  if (item.buildingType === "商业") {
    marketPrice = floor.marketPrice > 0 ? floor.marketPrice : undefined;
    if (marketPrice === undefined) {
      sink.error(
        "marketPrice",
        `缺少适用地价类型 ${applicablePriceType} 的市场价格。`,
        "请录入或导入对应市场价格。",
      );
    }
  } else {
    marketPrice = getMarketPrice(applicablePriceType, prices, sink);
  }

  // --- 3. Government-owned short-circuit --------------------------------------
  if (item.isGovernmentOwned) {
    sink.info("isGovernmentOwned", "该项产权归政府，不计收地价。");
    return buildEmptyResult(
      item,
      sink,
      applicablePriceType,
      marketPrice,
      floor.note,
    );
  }

  // --- 4. Other base factors ---------------------------------------------------
  const buildingTypeFactor = getBuildingTypeFactor(item.buildingType, sink);
  const landTermFactor = calculateLandTermFactor(
    applicablePriceType,
    item.landTermYears,
    sink,
  );
  const propertyConditionFactor = getPropertyConditionFactor(
    item.buildingType,
    item.propertyCondition,
    item.isAffordableHousing ?? false,
    sink,
  );
  const affordableHousingFactor = getAffordableHousingFactor(
    item.isAffordableHousing,
    item.affordableHousingType,
    sink,
  );
  // Industry orientation / industry project type are now wired through
  // the parcel so we can apply the project-type exclusion rules from
  // RULES_INDUSTRIAL.md §2 / §3.
  const industryOrientationFactor = getIndustryOrientationFactor(
    item,
    parcel,
    sink,
  );
  const industryProjectTypeFactor = getIndustryProjectTypeFactor(
    item,
    parcel,
    sink,
  );
  // Project-level factor (棚改 / 留用 / 城市更新 项目修正) is still out
  // of scope per CLAUDE.md §7.
  const projectFactor = DEFAULT_FACTOR;
  const undergroundSpaceFactor = getUndergroundSpaceFactor(
    item.isUnderground,
    item.undergroundType,
    sink,
  );

  // --- 5. Combine --------------------------------------------------------------
  const chargeableGfa = item.gfa;
  const finalFactor =
    buildingTypeFactor *
    landTermFactor *
    propertyConditionFactor *
    affordableHousingFactor *
    industryOrientationFactor *
    industryProjectTypeFactor *
    commercialFloorFactor *
    undergroundSpaceFactor *
    projectFactor;

  const numericMarketPrice = marketPrice ?? 0;
  const itemLandPrice = numericMarketPrice * chargeableGfa * finalFactor;
  const floorLandPrice = chargeableGfa > 0 ? itemLandPrice / chargeableGfa : 0;

  const chain: FactorChain = {
    itemId: item.id,
    marketPrice: numericMarketPrice,
    buildingTypeFactor,
    landTermFactor,
    propertyConditionFactor,
    affordableHousingFactor,
    industryOrientationFactor,
    industryProjectTypeFactor,
    commercialFloorFactor,
    undergroundSpaceFactor,
    projectFactor,
    finalFactor,
    notes: collectNotes(floor.note),
  };

  const formulaText = buildFormulaText({
    marketPrice: numericMarketPrice,
    chargeableGfa,
    finalFactor,
    itemLandPrice,
    floorNote: floor.note,
  });

  return {
    itemId: item.id,
    buildingType: item.buildingType,
    gfa: item.gfa,
    chargeableGfa,
    applicablePriceType,
    marketPrice: numericMarketPrice,
    factorChain: chain,
    itemLandPrice,
    floorLandPrice,
    formulaText,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildEmptyResult(
  item: BuildingItem,
  _sink: MessageSink,
  applicablePriceType: ReturnType<typeof getApplicablePriceType>,
  marketPrice: number | undefined,
  floorNote?: string,
): ItemCalculationResult {
  const chain: FactorChain = {
    itemId: item.id,
    marketPrice: marketPrice ?? 0,
    buildingTypeFactor: DEFAULT_FACTOR,
    landTermFactor: DEFAULT_FACTOR,
    propertyConditionFactor: DEFAULT_FACTOR,
    affordableHousingFactor: DEFAULT_FACTOR,
    industryOrientationFactor: DEFAULT_FACTOR,
    industryProjectTypeFactor: DEFAULT_FACTOR,
    commercialFloorFactor: DEFAULT_FACTOR,
    undergroundSpaceFactor: DEFAULT_FACTOR,
    projectFactor: DEFAULT_FACTOR,
    finalFactor: 0,
    notes: collectNotes(floorNote),
  };
  return {
    itemId: item.id,
    buildingType: item.buildingType,
    gfa: item.gfa,
    chargeableGfa: 0,
    // applicablePriceType may be undefined when the building type is
    // unknown; surface that explicitly rather than coercing to "未知".
    applicablePriceType:
      applicablePriceType ??
      ("未知" as ItemCalculationResult["applicablePriceType"]),
    marketPrice: marketPrice ?? 0,
    factorChain: chain,
    itemLandPrice: 0,
    floorLandPrice: 0,
    formulaText: "",
  };
}

function collectNotes(floorNote: string | undefined): string[] {
  return floorNote ? [floorNote] : [];
}

interface FormulaParts {
  marketPrice: number;
  chargeableGfa: number;
  finalFactor: number;
  itemLandPrice: number;
  floorNote?: string;
}

function buildFormulaText(p: FormulaParts): string {
  const tail = p.floorNote ? `（${p.floorNote}）` : "";
  return (
    `市场价 ${p.marketPrice} × 建筑面积 ${p.chargeableGfa} × ` +
    `综合系数 ${p.finalFactor} = ${p.itemLandPrice} 元${tail}`
  );
}
